import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Building2, TrendingUp, Mail, Phone, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const COLORS = ['#1E3A5F', '#304F9D', '#D4AF37', '#566FAF', '#9A7B10', '#A2B0D3', '#ECC14C'];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    dashboardAPI.stats()
      .then((r) => setStats(r.data))
      .catch(() => toast.error('İstatistikler yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-navy-200 border-t-navy-600 rounded-full animate-spin" />
    </div>
  );

  if (!stats) return null;

  const statCards = [
    {
      label: 'Toplam Firma',
      value: stats.toplam_firma.toLocaleString('tr-TR'),
      icon: Building2,
      color: 'bg-navy-600',
      action: () => navigate('/firmalar'),
    },
    {
      label: 'Bu Ay Eklenen',
      value: stats.bu_ay_eklenen.toLocaleString('tr-TR'),
      icon: TrendingUp,
      color: 'bg-emerald-600',
    },
    {
      label: 'Mail Eksik',
      value: stats.mail_eksik.toLocaleString('tr-TR'),
      icon: Mail,
      color: 'bg-amber-500',
      action: () => navigate('/firmalar?mail_eksik=true'),
    },
    {
      label: 'Telefon Eksik',
      value: stats.telefon_eksik.toLocaleString('tr-TR'),
      icon: Phone,
      color: 'bg-rose-500',
      action: () => navigate('/firmalar?tel_eksik=true'),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Başlık */}
      <div>
        <h1 className="text-2xl font-display text-navy-800">Dashboard</h1>
        <p className="text-navy-400 text-sm mt-1">Genel istatistikler ve özet bilgiler</p>
      </div>

      {/* Stat kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <div
            key={s.label}
            className={`stat-card ${s.action ? 'cursor-pointer hover:shadow-card-hover transition-shadow' : ''}`}
            onClick={s.action}
          >
            <div className={`w-10 h-10 rounded-xl ${s.color} flex items-center justify-center flex-shrink-0`}>
              <s.icon size={18} className="text-white" />
            </div>
            <div>
              <p className="text-2xl font-display text-navy-800 leading-none">{s.value}</p>
              <p className="text-navy-400 text-xs mt-1">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Aylık trend */}
      <div className="card p-6">
        <h2 className="text-base font-semibold text-navy-700 mb-4">Aylık Ekleme Trendi</h2>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={stats.aylik_eklemeler} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#566FAF', fontFamily: 'Outfit' }}
              axisLine={false} tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#566FAF', fontFamily: 'Outfit' }}
              axisLine={false} tickLine={false} allowDecimals={false}
            />
            <Tooltip
              formatter={(v) => [v, 'Firma']}
              labelStyle={{ color: '#1E3A5F', fontWeight: 600 }}
              contentStyle={{ fontFamily: 'Outfit', fontSize: 13 }}
            />
            <Bar dataKey="count" fill="#1E3A5F" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Alt satır — 2 kolon */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Kurum dağılımı */}
        <div className="card p-6">
          <h2 className="text-base font-semibold text-navy-700 mb-4">Kurum Dağılımı</h2>
          {stats.kurum_dagilim.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-navy-300 text-sm">
              <AlertCircle size={16} className="mr-2" /> Henüz kurum kaydı yok
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={stats.kurum_dagilim}
                  dataKey="count"
                  nameKey="ad"
                  cx="40%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={40}
                >
                  {stats.kurum_dagilim.map((entry, i) => (
                    <Cell key={i} fill={entry.renk || COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 12, fontFamily: 'Outfit' }}
                />
                <Tooltip
                  formatter={(v, n) => [v + ' firma', n]}
                  contentStyle={{ fontFamily: 'Outfit', fontSize: 13 }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* İlçe/OSB top 10 */}
        <div className="card p-6">
          <h2 className="text-base font-semibold text-navy-700 mb-4">İlçe/OSB Dağılımı (Top 10)</h2>
          {stats.ilce_dagilim.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-navy-300 text-sm">
              <AlertCircle size={16} className="mr-2" /> Henüz ilçe kaydı yok
            </div>
          ) : (
            <div className="space-y-2.5 overflow-y-auto max-h-52">
              {stats.ilce_dagilim.map((row, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-mono text-navy-400 w-4 text-right">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-0.5">
                      <span className="text-navy-700 font-medium truncate">{row.ad}</span>
                      <span className="text-navy-500 flex-shrink-0 ml-2">{row.count}</span>
                    </div>
                    <div className="h-1.5 bg-navy-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-navy-600 rounded-full transition-all duration-500"
                        style={{ width: `${(row.count / stats.ilce_dagilim[0].count) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
